import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RelatedListSidebar, NotesPanel, type RelatedTab } from "@/components/related-list-sidebar";
import { ActivitiesPanel } from "@/components/activities-panel";
import { TagsPanel } from "@/components/tags-panel";
import { Mail, Trash2, PhoneCall } from "lucide-react";
import { EmailComposer } from "@/components/email-composer";
import { AttachmentsPanel } from "@/components/attachments-panel";
import { QuickAddTask } from "@/components/quick-add-task";
import { QuickAddMeeting } from "@/components/quick-add-meeting";
import { QuickLogActivity } from "@/components/quick-log-activity";
import { ReportsToTreePanel } from "@/components/hierarchy-tree";

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-sm text-right">{v}</span>
    </div>
  );
}

function Address({ label, street, city, state, zip, country }: {
  label: string; street?: string | null; city?: string | null; state?: string | null; zip?: string | null; country?: string | null;
}) {
  if (!street && !city && !state && !zip && !country) return null;
  const parts = [city, state, zip, country].filter(Boolean).join(", ");
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm">
        {street && <div>{street}</div>}
        {parts && <div>{parts}</div>}
      </div>
    </div>
  );
}

const TABS: RelatedTab[] = ["notes", "activities", "meetings", "tasks", "hierarchy", "attachments", "emails", "tags"];

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<RelatedTab>("notes");
  const [composerOpen, setComposerOpen] = useState(false);

  const queryKey = ["/contacts", id];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(`/contacts/${id}`)).data,
    enabled: !!id,
  });

  async function del() {
    if (!data || !confirm(`Delete contact "${data.name}"?`)) return;
    await api.delete(`/contacts/${id}`);
    qc.invalidateQueries({ queryKey: ["/contacts"] });
    navigate("/contacts");
  }
  async function addToQueue() {
    if (!confirm("Add this contact to your call queue? You'll become the owner.")) return;
    await api.post("/call-queue/add", { kind: "contact", ids: [id] });
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["/call-queue"] });
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;

  return (
    <>
      <PageHeader
        title={`${data.salutation ? data.salutation + " " : ""}${data.name}`}
        description={data.title ?? undefined}
        actions={
          <>
            <Button variant="outline" onClick={() => setComposerOpen(true)}><Mail className="mr-1 h-4 w-4"/>Send Email</Button>
            <Button variant="outline" onClick={addToQueue}><PhoneCall className="mr-1 h-4 w-4"/>Add to my queue</Button>
            <Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4"/>Delete</Button>
          </>
        }
      />

      <div className="flex flex-1 min-h-[70vh]">
        <RelatedListSidebar
          tabs={TABS}
          active={tab}
          onSelect={setTab}
          counts={{ tags: data.tags?.length } as any}
        />

        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Identity</CardTitle></CardHeader>
              <CardContent>
                <KV k="First name" v={data.firstName} />
                <KV k="Last name" v={data.lastName} />
                <KV k="Title" v={data.title} />
                <KV k="Degree" v={data.degree} />
                <KV k="Account" v={data.account ? <Link className="hover:underline" to={`/accounts/${data.account.id}`}>{data.account.name}</Link> : null} />
                <KV k="Owner" v={data.owner?.name} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Contact channels</CardTitle></CardHeader>
              <CardContent>
                <KV k="Email" v={data.email ? <a className="hover:underline" href={`mailto:${data.email}`}>{data.email}</a> : null} />
                <KV k="Phone" v={data.phone} />
                <KV k="Mobile" v={data.mobile} />
                <KV k="LinkedIn" v={data.linkedinUrl ? <a className="hover:underline" href={data.linkedinUrl} target="_blank" rel="noreferrer">Profile</a> : null} />
                <KV k="Preferred" v={data.communicationPref} />
                <KV k="Influence" v={data.influenceRole?.replace(/_/g, " ")} />
                <KV k="Opted out" v={data.optedOut ? "Yes" : null} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Attribution</CardTitle></CardHeader>
              <CardContent>
                <KV k="Lead source" v={data.leadSource} />
                <KV k="Geography" v={data.contactGeography} />
                <KV k="Solutions" v={data.solutions} />
                <KV k="CBC offerings" v={data.cbcOfferings} />
              </CardContent>
            </Card>

            {(data.mailingStreet || data.otherStreet) && (
              <Card>
                <CardHeader><CardTitle className="text-base">Addresses</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Address label="Mailing" street={data.mailingStreet} city={data.mailingCity} state={data.mailingState} zip={data.mailingZip} country={data.mailingCountry} />
                  <Address label="Other"   street={data.otherStreet}   city={data.otherCity}   state={data.otherState}   zip={data.otherZip}   country={data.otherCountry} />
                </CardContent>
              </Card>
            )}

            {data.description && (
              <Card className="md:col-span-2">
                <CardHeader><CardTitle className="text-base">Description</CardTitle></CardHeader>
                <CardContent><p className="whitespace-pre-wrap text-sm">{data.description}</p></CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base capitalize">{tab}</CardTitle></CardHeader>
            <CardContent>
              {tab === "notes" && <NotesPanel entityType="Contact" entityId={data.id} />}
              {tab === "activities" && (
                <div className="space-y-3">
                  <QuickLogActivity
                    contactId={data.id}
                    accountId={data.account?.id}
                    invalidateQueryKeys={[["/activities", { field: "contactId", id: data.id }]]}
                  />
                  <ActivitiesPanel field="contactId" id={data.id} />
                </div>
              )}
              {tab === "meetings" && (
                <div className="space-y-3">
                  <QuickAddMeeting
                    contactId={data.id}
                    accountId={data.account?.id}
                    invalidateQueryKeys={[["/activities", { field: "contactId", id: data.id, type: "MEETING" }]]}
                  />
                  <ActivitiesPanel field="contactId" id={data.id} type="MEETING" emptyMessage="No meetings yet." />
                </div>
              )}
              {tab === "tasks" && (
                <div className="space-y-3">
                  <QuickAddTask
                    contactId={data.id}
                    accountId={data.account?.id}
                    invalidateQueryKeys={[["/activities", { field: "contactId", id: data.id, type: "TASK" }]]}
                  />
                  <ActivitiesPanel field="contactId" id={data.id} type="TASK" emptyMessage="No tasks yet." />
                </div>
              )}
              {tab === "emails" && (
                <div className="space-y-3">
                  <QuickLogActivity
                    contactId={data.id}
                    accountId={data.account?.id}
                    kinds={["EMAIL"]}
                    submitLabel="Log email"
                    invalidateQueryKeys={[["/activities", { field: "contactId", id: data.id, type: "EMAIL" }]]}
                  />
                  <ActivitiesPanel field="contactId" id={data.id} type="EMAIL" emptyMessage="No email history yet. Log emails you sent from Outlook via the button above, or wait for M365 sync." />
                </div>
              )}
              {tab === "attachments" && <AttachmentsPanel entityType="Contact" entityId={data.id} />}
              {tab === "hierarchy"   && <ReportsToTreePanel contactId={data.id} />}
              {tab === "tags"        && <TagsPanel endpoint={`/contacts/${data.id}`} queryKey={queryKey} tags={data.tags ?? []} />}
            </CardContent>
          </Card>
        </div>
      </div>

      <EmailComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        contact={data}
        invalidateQueryKeys={[["/activities", { field: "contactId", id: data.id }]]}
      />
    </>
  );
}
